import axios from "axios";

const baseUrl = window.env.API_URL;
const identityUrl = window.env.IDENTITY_URL;

const appId = "analyticsapi";
const apiVer = "2.8.8";

class analyticsApiClient {
  constructor() {
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.init();
  }

  init() {
    let options = {
      baseURL: baseUrl,
      withCredentials: true, // Enable cookies for refresh token
    };

    let token = JSON.parse(localStorage.getItem("authToken"));
    if (token) {
      options.headers = {
        Authorization: `Bearer ${token}`,
      };
    }

    this.client = axios.create(options);
    this.setupInterceptors();
  }

  setupInterceptors() {
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If 401 and not already retrying, try to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // Wait for refresh to complete, then retry
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.client(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const newToken = await this.refreshToken();
            if (newToken) {
              // Update stored token
              localStorage.setItem("authToken", JSON.stringify(newToken));
              this.client.defaults.headers.Authorization = `Bearer ${newToken}`;
              originalRequest.headers.Authorization = `Bearer ${newToken}`;

              // Notify subscribers
              this.refreshSubscribers.forEach((callback) => callback(newToken));
              this.refreshSubscribers = [];

              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.logout();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Not a 401 or refresh failed
        if (error.response?.status === 401) {
          this.logout();
        }

        return Promise.reject(error);
      },
    );
  }

  async refreshToken() {
    try {
      const response = await axios.post(
        `${identityUrl}/accesstoken/refresh`,
        {
          appId: appId,
          apiVersion: apiVer,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      return response.data.access_token;
    } catch (ex) {
      return null;
    }
  }

  logout() {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    window.location.href = "/login";
  }

  async auth(username, password) {
    try {
      const params = new URLSearchParams();
      params.append("username", username);
      params.append("password", password);
      params.append("appid", appId);
      params.append("apiver", apiVer);

      let response = await axios.post(`${identityUrl}/accesstoken`, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true, // Receive refresh token cookie
      });

      // Return the identity body verbatim: { access_token } on direct login, or
      // { requires_two_factor, two_factor_token, two_factor_method, ... } for a 2FA challenge.
      return response.data;
    } catch (ex) {
      throw ex;
    }
  }

  // Completes a 2FA-challenged login with an authenticator code or a one-time backup code.
  // The identity server verifies the code and falls back to backup codes itself. Returns the
  // identity body ({ access_token, ... }); withCredentials keeps the refresh-token cookie flow.
  async verify2fa({ twoFactorToken, code, method = "Authenticator" }) {
    let response = await axios.post(
      `${identityUrl}/accesstoken/verify2fa`,
      {
        twoFactorToken,
        code,
        method,
        appId: appId,
        apiVersion: apiVer,
      },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );
    return response.data;
  }

  async get_profile() {
    let response = await this.client.get("api/users/profile");
    return response.data;
  }

  async get_report_embedding_data() {
    let response = await this.client.get("api/reports");
    return response.data;
  }

  async get_tenants() {
    let response = await this.client.get("api/tenants");
    return response.data;
  }

  async onboard_tenant(data) {
    let response = await this.client.post("api/tenants/create", data);
    return response.data;
  }

  async delete_tenant(id) {
    let response = await this.client.delete(`api/tenants/delete/${id}`);
    return response.data;
  }

  async get_tenant_report_embedding_data(id) {
    let response = await this.client.get(`api/tenants/reports/${id}`);
    return response.data;
  }

  async export_report(data) {
    let response = await this.client.post("api/reports/exportfile", data, {
      responseType: "blob",
    });

    const header = response.headers.get("Content-Disposition");
    const parts = header.split(";");
    let filename = parts[1].split("=")[1].replaceAll('"', "");

    var url = window.URL.createObjectURL(response.data);
    var tempLink = document.createElement("a");
    tempLink.href = url;
    tempLink.download = filename;
    tempLink.click();
    tempLink.remove();

    return;
  }

  async refresh_data(id) {
    if (id) {
      let response = await this.client.post(`api/tenants/extractdata?id=${id}`);
      return response.data;
    } else {
      let response = await this.client.post(`api/tenants/extractdata`);
      return response.data;
    }
  }

  async refresh_report(id) {
    if (id) {
      let response = await this.client.post(
        `api/tenants/refreshreport?id=${id}`,
      );
      return response.data;
    } else {
      let response = await this.client.post(`api/tenants/refreshreport`);
      return response.data;
    }
  }

  async upload(file) {
    let formData = new FormData();

    formData.append("file", file);

    return await this.client.post("api/tenants/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async get_files() {
    let response = await this.client.get("api/tenants/files");
    return response.data;
  }
}

export default new analyticsApiClient();
